import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  Paper,
  Typography,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { VaccineRef, typicodeApi } from 'shared/api';
import { useVaccineSearch } from 'shared/lib';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const LOADING_VALUE = '__loading__';

type OptionType = {
  label: string;
  value: string;
};

type Props = {
  label: string;
  name: string;
  values: VaccineRef[];
  onChange: (name: string, values: VaccineRef[]) => void;
  isPets?: boolean;
  error?: boolean;
  helperText?: ReactNode;
};

const QUERY_KEY_HUMAN = 'vaccine-search-human';
const QUERY_KEY_PETS = 'vaccine-search-pets';

const LOADING_OPTION: OptionType = { label: '', value: LOADING_VALUE };

// Factory for stable PaperComponent — reading isSearching from ref avoids re-creation
const createSearchPaper = (isSearchingRef: React.MutableRefObject<boolean>) => {
  const SearchPaper = (props: React.HTMLAttributes<HTMLElement>) => (
    <Paper {...props} style={{ ...props.style, position: 'relative' }}>
      {isSearchingRef.current && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1 }} />}
      {props.children}
    </Paper>
  );
  return SearchPaper;
};

export const VaccineAutocomplete = ({ label, name, values, onChange, isPets = false, error, helperText }: Props) => {
  const fetchFn = isPets ? typicodeApi.vaccinePets.getVaccinesAll : typicodeApi.vaccine.getVaccinesAll;
  const queryKey = isPets ? QUERY_KEY_PETS : QUERY_KEY_HUMAN;

  const {
    options: rawOptions,
    setSearchText,
    isLoading,
    isSearching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useVaccineSearch({ fetchFn, queryKey });

  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const [pendingRemoval, setPendingRemoval] = useState<OptionType | null>(null);

  // Guard against false blur triggered by chip expansion on open
  const preventCloseRef = useRef(false);

  const handleOpen = useCallback(() => {
    setOpen(true);
    preventCloseRef.current = true;
    setTimeout(() => {
      preventCloseRef.current = false;
    }, 300);
  }, []);

  const handleClose = useCallback(() => {
    if (preventCloseRef.current) return;
    setOpen(false);
  }, []);

  const isSearchingRef = useRef(false);
  isSearchingRef.current = isSearching;

  // Stable PaperComponent reference — prevents dropdown remount on re-render
  const SearchPaper = useMemo(() => createSearchPaper(isSearchingRef), []);

  // Cache: option value (id) → OptionType for preserving labels across search/pagination
  const knownOptionsRef = useRef<Map<string, OptionType>>(new Map());

  const mappedOptions = useMemo(() => {
    return rawOptions.map((item) => ({
      label: item.name,
      value: item.id,
    }));
  }, [rawOptions]);

  // Sync known options cache when mapped options change
  useEffect(() => {
    mappedOptions.forEach((opt) => knownOptionsRef.current.set(opt.value, opt));
  }, [mappedOptions]);

  // Append a loading sentinel at the end when fetching next page
  const displayOptions = useMemo(() => {
    if (!isFetchingNextPage) return mappedOptions;
    return [...mappedOptions, LOADING_OPTION];
  }, [mappedOptions, isFetchingNextPage]);

  // Map VaccineRef[] → OptionType[] for Autocomplete value
  const selectedOptions = useMemo(() => {
    return values.map((ref) => {
      const opt: OptionType = { label: ref.name, value: ref._id };
      knownOptionsRef.current.set(opt.value, opt);
      return opt;
    });
  }, [values]);

  const handleListScroll = useCallback(
    (event: React.UIEvent<HTMLUListElement>) => {
      const target = event.currentTarget;
      if (target.scrollHeight - target.scrollTop - target.clientHeight < 50 && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  // Map OptionType back to VaccineRef for the parent
  const vaccineRefMapRef = useRef<Map<string, VaccineRef>>(new Map());

  useEffect(() => {
    values.forEach((ref) => vaccineRefMapRef.current.set(ref._id, ref));
  }, [values]);

  useEffect(() => {
    rawOptions.forEach((item) =>
      vaccineRefMapRef.current.set(item.id, { _id: item.id, name: item.name, name_ru: item.name }),
    );
  }, [rawOptions]);

  return (
    <>
      <Autocomplete
        sx={{
          '& .MuiAutocomplete-endAdornment': {
            display: 'none',
          },
        }}
        fullWidth
        limitTags={5}
        multiple
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        loading={isLoading}
        inputValue={inputValue}
        options={displayOptions}
        value={selectedOptions}
        disableCloseOnSelect
        filterOptions={(x) => x}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        getOptionLabel={(option) => option.label}
        getOptionDisabled={(option) => option.value === LOADING_VALUE}
        ListboxProps={{ onScroll: handleListScroll }}
        PaperComponent={SearchPaper}
        onInputChange={(_, value, reason) => {
          if (reason === 'input') {
            setInputValue(value);
            setSearchText(value);
          }
        }}
        renderOption={(props, option, { selected }) => {
          if (option.value === LOADING_VALUE) {
            return (
              <li key={LOADING_VALUE} style={{ display: 'flex', justifyContent: 'center', padding: 8 }}>
                <CircularProgress size={20} />
              </li>
            );
          }
          return (
            <li {...props} key={option.value}>
              <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
              <Typography>{option.label}</Typography>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField {...params} label={label} name={name} margin="dense" error={error} helperText={helperText} />
        )}
        onChange={(_, v, reason, details) => {
          const selected = (v as OptionType[]).filter((item) => item.value !== LOADING_VALUE);

          // Подтверждение только при удалении через крестик чипа (дропдаун закрыт)
          if (reason === 'removeOption' && details?.option && !open) {
            setPendingRemoval(details.option);
            return;
          }

          selected.forEach((item) => knownOptionsRef.current.set(item.value, item));
          setInputValue('');
          setSearchText('');
          const vaccineRefs = selected.map((opt) => {
            const existing = vaccineRefMapRef.current.get(opt.value);
            if (existing) return existing;
            return { _id: opt.value, name: opt.label, name_ru: opt.label };
          });
          onChange(name, vaccineRefs);
        }}
      />
      <Dialog open={!!pendingRemoval} onClose={() => setPendingRemoval(null)}>
        <DialogTitle>Confirm removal</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove vaccine &quot;{pendingRemoval?.label}&quot;?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingRemoval(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              const updated = values.filter((ref) => ref._id !== pendingRemoval?.value);
              onChange(name, updated);
              setPendingRemoval(null);
            }}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
